import React, { useEffect, useRef, useState } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import styles from "./VideoPlayer.module.css";

type Props = {
  previewImg?: string;
  src: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  showPreview?: boolean;    // preview + progress (like existing Player)
  showProgress?: boolean;
  darkenOnScroll?: boolean; // curtain effect
  darkenMax?: number;       // 0..1, default 1.0
  fadeRange?: number;       // pixels from center where fade completes
  className?: string;
  style?: React.CSSProperties;
};

const VideoPlayer: React.FC<Props> = React.memo(({
  previewImg,
  src,
  autoplay = true,
  loop = false,
  muted = true,
  controls = false,
  showPreview = false,
  showProgress = false,
  darkenOnScroll = false,
  darkenMax = 1,
  fadeRange = 400,
  className,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const preloadRef = useRef<HTMLVideoElement | null>(null); // hidden preload (like old Player)
  const [progress, setProgress] = useState(0);
  const [imageScale, setImageScale] = useState(0.2);
  const [ready, setReady] = useState(false);
  const rafRef = useRef<number | null>(null);
  const ticking = useRef(false);
  const lastOpacity = useRef<number>(darkenMax);
  const lastScrollY = useRef<number>(window.scrollY);
  const openedRef = useRef<boolean>(false);

  // direct ref to the curtain overlay (avoid querySelector / CSS-module lookup)
  const overlayRef = useRef<HTMLDivElement | null>(null);
  
  // preview animation (image zoom) — only when showPreview true
  useEffect(() => {
    if (!showPreview) return;
    let scale = 0.2;
    const id = window.setInterval(() => {
      if (scale < 1) {
        scale += 0.01;
        setImageScale(Math.min(scale, 1));
      } else {
        clearInterval(id);
      }
    }, 40);
    return () => clearInterval(id);
  }, [showPreview]);

  // progress simulation
  useEffect(() => {
    if (!showProgress) return;
    let intervalId: number | undefined;
    let finishId: number | undefined;

    if (!ready) {
      intervalId = window.setInterval(() => {
        setProgress((prev) => (prev < 90 ? prev + 1 : prev));
      }, 120);
    } else {
      finishId = window.setInterval(() => {
        setProgress((prev) => {
          if (prev < 100) return Math.min(100, prev + 2);
          if (finishId) window.clearInterval(finishId);
          return 100;
        });
      }, 40);
    }

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      if (finishId) window.clearInterval(finishId);
    };
  }, [ready, showProgress]);

  // Preload video
  useEffect(() => {
    let v = preloadRef.current;
    if (!v) return;
    const onReady = () => setReady(true);
    v.addEventListener("canplaythrough", onReady);
    try {
      v.load();
      v.play().catch(() => {});
    } catch {}
    return () => {
      if (v) {
        v.removeEventListener("canplaythrough", onReady);
      }
    };
  }, []);

  // Autoplay handling
  useEffect(() => {
    if (!autoplay) return;
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = async () => {
      try {
        await v.play();
      } catch {}
    };
    if (ready) tryPlay();
    else v.play().catch(() => {});
  }, [autoplay, ready]);

  // Curtain effect (fade on scroll)
  useEffect(() => {
    if (!darkenOnScroll) return;
    const el = containerRef.current;
    if (!el) return;

    // set initial opacity on the overlay ref if present
    const overlay = overlayRef.current ?? el.querySelector<HTMLDivElement>('.' + (styles["video-curtain"] ?? "video-curtain"));
    if (overlay) overlay.style.opacity = `${darkenMax}`;
    lastOpacity.current = darkenMax;
    openedRef.current = false;
  
    const onScroll = () => {
      if (openedRef.current) return;
      const currentY = window.scrollY;
      const dy = currentY - lastScrollY.current;
      lastScrollY.current = currentY;
      if (dy <= 0) return;
  
      if (ticking.current) return;
      ticking.current = true;
      rafRef.current = requestAnimationFrame(() => {
        if (!el) {
          ticking.current = false;
          return;
        }
        const rect = el.getBoundingClientRect();
        const elCenter = rect.top + rect.height / 2;
        const vpCenter = window.innerHeight / 2;
        const dist = Math.abs(vpCenter - elCenter);
        const norm = Math.min(1, dist / fadeRange);
        const eased = Math.pow(norm, 1.0);
        const computedOpacity = Math.min(darkenMax, Math.max(0, eased * darkenMax));
        const newOpacity = Math.min(lastOpacity.current, computedOpacity);
        const currentOverlay = overlayRef.current ?? el.querySelector<HTMLDivElement>('.' + (styles["video-curtain"] ?? "video-curtain"));
        if (currentOverlay) currentOverlay.style.opacity = `${newOpacity}`;
        lastOpacity.current = newOpacity;
        if (newOpacity <= 0.01) {
          openedRef.current = true;
          if (currentOverlay) currentOverlay.style.opacity = `0`;
        }
        ticking.current = false;
      });
    };
  
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [darkenOnScroll, darkenMax, fadeRange]);

  const isSmallScreen = typeof window !== "undefined" ? window.innerWidth <= 768 : false;

  const frameStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    aspectRatio: style?.aspectRatio ?? "4/5",
    minHeight: isSmallScreen ? 220 : 320,
    background: "#fff",
    borderRadius: 20,
    overflow: "hidden",
  };

  // use css-module classes
  const modeClass = darkenOnScroll ? styles["video--cover"] : styles["video--contain"];
  const rootClass = [styles["video-player"], modeClass, className].filter(Boolean).join(" ");

  return (
    <div
      ref={containerRef}
      className={rootClass}
      style={{
        position: "relative",
        width: "100%",
        height: "auto",
        maxHeight: "100%",
        overflow: "visible",
        borderRadius: 20,
        cursor: "pointer",
        margin: "auto",
        ...style,
      }}
    >
      {/* hidden preload video */}
      <video
        ref={preloadRef}
        className={styles["video-preload"]}
        src={src}
        style={{ display: "none" }}
        preload="auto"
        muted
        autoPlay
        playsInline
      />

      {/* FRAME (keeps aspect, min-height and consistent layout similar to old Player) */}
      <div className={styles["player-frame"]} style={frameStyle}>
        {/* preview + progress */}
        {showPreview && !ready && previewImg && (
          <>
            <img
              src={previewImg}
              alt="preview"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 20,
                transform: `scale(${imageScale})`,
                transition: "transform 40ms linear",
                zIndex: 2,
              }}
            />
            {showProgress && (
              <div className={styles["progress-overlay"]}>
                <CircularProgressbar
                  value={progress}
                  text={`${progress}%`}
                  styles={buildStyles({
                    textColor: "#31d5c8",
                    pathColor: "#31d5c8",
                    trailColor: "#eee",
                    textSize: "1.2rem",
                  })}
                />
              </div>
            )}
          </>
        )}

        {/* visible video: CSS controls cover/contain */}
        <video
          ref={videoRef}
          src={src}
          className={styles["video-visible"]}
          autoPlay={autoplay}
          muted={muted}
          loop={loop}
          controls={controls}
          playsInline
        />

        {/* curtain overlay */}
        {darkenOnScroll && (
          <div
            ref={overlayRef}
            className={styles["video-curtain"]}
            style={{
              opacity: darkenMax,
              transition: "opacity 120ms ease-in-out",
            }}
          />
        )}
      </div>
    </div>
  );
});

export default VideoPlayer;